import React from 'react';
import PropTypes from 'prop-types';

const SeoContext = React.createContext(null);

const setAttribute = (element, name, value) => {
  if (value !== null && value !== undefined) {
    element.setAttribute(name, value);
  }
};

const applySeoToDocument = ({
  title,
  description = '',
  canonical = '',
  index = true,
  follow = true,
  ogType = 'website',
  ogImage = null,
  twitterCard = 'summary_large_image',
  alternates = [],
  jsonLd = null,
}) => {
  document.querySelectorAll('[data-mk-seo]').forEach((element) => element.remove());

  const append = (tagName, attributes = {}, content = null) => {
    const element = document.createElement(tagName);
    Object.entries(attributes).forEach(([name, value]) => setAttribute(element, name, value));
    element.dataset.mkSeo = '';
    if (content !== null) {
      element.textContent = content;
    }
    document.head.appendChild(element);
  };

  const robots = [
    index ? 'index' : 'noindex',
    follow ? 'follow' : 'nofollow',
    ...(index ? ['max-image-preview:large'] : []),
  ].join(',');

  document.title = title;
  append('meta', { name: 'description', content: description });
  append('meta', { name: 'robots', content: robots });
  if (canonical) append('link', { rel: 'canonical', href: canonical });
  alternates.forEach((alternate) => {
    append('link', {
      rel: 'alternate',
      hreflang: alternate.hrefLang,
      href: alternate.href,
    });
  });
  append('meta', { property: 'og:type', content: ogType });
  append('meta', { property: 'og:title', content: title });
  append('meta', { property: 'og:description', content: description });
  if (canonical) append('meta', { property: 'og:url', content: canonical });
  if (ogImage) append('meta', { property: 'og:image', content: ogImage });
  append('meta', { name: 'twitter:card', content: twitterCard });
  append('meta', { name: 'twitter:title', content: title });
  append('meta', { name: 'twitter:description', content: description });
  if (ogImage) append('meta', { name: 'twitter:image', content: ogImage });
  if (jsonLd) append('script', { type: 'application/ld+json' }, JSON.stringify(jsonLd));
};

export const SeoProvider = ({ children, onSeoChange = null }) => (
  <SeoContext.Provider value={onSeoChange}>
    {children}
  </SeoContext.Provider>
);

SeoProvider.propTypes = {
  children: PropTypes.node.isRequired,
  onSeoChange: PropTypes.func,
};

const SEO = ({
  title,
  description = '',
  canonical = '',
  index = true,
  follow = true,
  ogType = 'website',
  ogImage = null,
  twitterCard = 'summary_large_image',
  alternates = [],
  jsonLd = null,
}) => {
  const onSeoChange = React.useContext(SeoContext);
  const values = React.useMemo(() => ({
    title,
    description,
    canonical,
    index,
    follow,
    ogType,
    ogImage,
    twitterCard,
    alternates,
    jsonLd,
  }), [
    title,
    description,
    canonical,
    index,
    follow,
    ogType,
    ogImage,
    twitterCard,
    alternates,
    jsonLd,
  ]);

  if (onSeoChange) {
    onSeoChange(values);
  }

  React.useEffect(() => {
    if (!onSeoChange) {
      applySeoToDocument(values);
    }
  }, [onSeoChange, values]);

  return null;
};

SEO.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  canonical: PropTypes.string,
  index: PropTypes.bool,
  follow: PropTypes.bool,
  ogType: PropTypes.string,
  ogImage: PropTypes.string,
  twitterCard: PropTypes.string,
  alternates: PropTypes.arrayOf(
    PropTypes.shape({
      hrefLang: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })
  ),
  jsonLd: PropTypes.object,
};

export default SEO;
